import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Github, Twitter, Linkedin, Mail } from 'lucide-react';
const sections = [{
  title: '1. Information We Collect',
  content: `When you use our services or contact us, we may collect the following types of information:

• Personal identification information: name, email address, phone number
• Project-related information you share with us voluntarily
• Technical data: IP address, browser type, device information, and usage data collected through cookies and similar technologies
• Communication records: emails, messages, and other correspondence`
}, {
  title: '2. How We Use Your Information',
  content: `We use the information we collect for the following purposes:

• To provide, maintain, and improve our services
• To communicate with you about projects, inquiries, and support
• To send relevant updates about our services (with your consent)
• To comply with legal obligations
• To protect our rights and prevent misuse of our services`
}, {
  title: '3. Data Sharing & Third Parties',
  content: `We do not sell your personal data. We may share information with:

• Service providers who assist in delivering our services (hosting, analytics, email)
• Legal authorities when required by law or to protect our rights
• Business partners, only with your explicit consent

All third-party providers are bound by data processing agreements in compliance with GDPR.`
}, {
  title: '4. Cookies & Tracking',
  content: `Our website may use cookies and similar tracking technologies to enhance your experience. These include:

• Essential cookies: required for the website to function properly
• Analytics cookies: help us understand how visitors interact with our site
• Preference cookies: remember your settings and preferences

You can manage cookie preferences through your browser settings at any time.`
}, {
  title: '5. Data Retention',
  content: `We retain your personal data only for as long as necessary to fulfill the purposes outlined in this policy, or as required by law. Project-related data is retained for the duration of our business relationship and a reasonable period thereafter for legal and administrative purposes.`
}, {
  title: '6. Your Rights',
  content: `Under GDPR and applicable Belgian law, you have the right to:

• Access the personal data we hold about you
• Request correction of inaccurate data
• Request deletion of your data ("right to be forgotten")
• Restrict or object to processing of your data
• Data portability — receive your data in a structured format
• Withdraw consent at any time

To exercise any of these rights, contact us at hello@taptheory.be.`
}, {
  title: '7. Data Security',
  content: `We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.`
}, {
  title: '8. Changes to This Policy',
  content: `We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically.`
}, {
  title: '9. Contact',
  content: `If you have questions about this Privacy Policy or your personal data, contact us at:

Tap Theory
Email: hello@taptheory.be
Website: taptheory.be`
}];
export function PrivacyPage() {
  return <div className="min-h-screen bg-background font-sans text-text-main flex flex-col" data-id="element-254">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-white/5" data-id="element-255">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-256">
          <div className="flex justify-between items-center h-20" data-id="element-257">
            <Link to="/" className="font-mono text-xl font-bold tracking-tighter text-white" data-id="element-258">
              taptheory<span className="text-primary" data-id="element-259">.be</span>
            </Link>
            <Link to="/" className="flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-white transition-colors" data-id="element-260">
              <ArrowLeft className="w-4 h-4" data-id="element-261" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className="flex-1 pt-32 pb-24" data-id="element-262">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-263">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="mb-12" data-id="element-264">
            <span className="font-mono text-sm font-bold text-primary tracking-widest uppercase block mb-3" data-id="element-265">
              Legal
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-4" data-id="element-266">
              Privacy Policy
            </h1>
            <p className="text-gray-400" data-id="element-267">
              Last updated:{' '}
              {new Date().toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="mb-10 text-gray-300 leading-relaxed" data-id="element-268">
            Tap Theory ("we", "us", or "our") is committed to protecting your
            privacy. This Privacy Policy explains how we collect, use, and
            safeguard your personal information when you visit our website or
            use our services.
          </motion.div>

          <div className="space-y-10" data-id="element-269">
            {sections.map((section, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.15 + i * 0.05
          }} data-id="element-270">
                <h2 className="text-xl font-bold text-white mb-4" data-id="element-271">
                  {section.title}
                </h2>
                <p className="text-gray-400 leading-relaxed whitespace-pre-line" data-id="element-272">
                  {section.content}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 bg-black" data-id="element-273">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-274">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8" data-id="element-275">
            <span className="font-mono text-xl font-bold tracking-tighter text-white" data-id="element-276">
              taptheory<span className="text-primary" data-id="element-277">.be</span>
            </span>
            <div className="flex space-x-8 text-sm font-medium text-gray-400" data-id="element-278">
              <Link to="/contact" className="hover:text-primary transition-colors" data-id="element-279">
                Contact
              </Link>
              <Link to="/privacy" className="text-primary transition-colors" data-id="element-280">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-primary transition-colors" data-id="element-281">
                Terms
              </Link>
            </div>
            <div className="flex space-x-6" data-id="element-282">
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-283">
                <Github className="w-5 h-5" data-id="element-284" />
              </a>
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-285">
                <Twitter className="w-5 h-5" data-id="element-286" />
              </a>
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-287">
                <Linkedin className="w-5 h-5" data-id="element-288" />
              </a>
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-289">
                <Mail className="w-5 h-5" data-id="element-290" />
              </a>
            </div>
          </div>
          <div className="mt-8 text-center text-xs text-gray-600 font-mono" data-id="element-291">
            &copy; {new Date().getFullYear()} Tap Theory. All rights reserved.
          </div>
        </div>
      </footer>
    </div>;
}